import { signal } from '@angular/core';
import { Observable } from 'rxjs';

export interface PendingFlag {
  isPending: () => boolean;
  setPending: () => void;
  resetPending: () => void;
}

export const pendingFlag = (): PendingFlag => {
  const pending = signal<boolean>(false);

  const isPending = () => pending();

  const setPending = () => pending.set(true);

  const resetPending = () => pending.set(false);

  return {
    isPending,
    setPending,
    resetPending,
  };
};

export const withPendingFlag =
  (...flags: PendingFlag[]) =>
  <T>(source: Observable<T>) =>
    new Observable<T>((subscriber) => {
      flags.forEach((f) => f.setPending());

      return source.subscribe({
        next: (value) => {
          subscriber.next(value);
          flags.forEach((f) => f.resetPending());
        },
        error: (error) => {
          subscriber.error(error);
          flags.forEach((f) => f.resetPending());
        },
        complete: () => subscriber.complete(),
      });
    });

export interface PendingFlagGroup {
  isPending: (id: string) => boolean;
  isAnyPending: () => boolean;
  addPending: (id: string) => void;
  removePending: (id: string) => void;
}

export const pendingFlagGroup = (): PendingFlagGroup => {
  const pendingList = signal<string[]>([]);

  const isPending = (id: string) => pendingList().includes(id);

  const isAnyPending = () => pendingList().length > 0;

  const addPending = (id: string) => pendingList.update((currentPending) => [...currentPending, id]);

  const removePending = (id: string) =>
    pendingList.update((currentPending) => currentPending.filter((currentPendingId) => currentPendingId !== id));

  return {
    isPending,
    isAnyPending,
    addPending,
    removePending,
  };
};

export const withPendingFlagGroup =
  (itemId: string, ...flags: PendingFlagGroup[]) =>
  <T>(source: Observable<T>) =>
    new Observable<T>((subscriber) => {
      flags.forEach((f) => f.addPending(itemId));

      return source.subscribe({
        next: (value) => {
          subscriber.next(value);
        },
        error: (error) => {
          subscriber.error(error);
        },
        complete: () => {
          flags.forEach((f) => f.removePending(itemId));
        },
      });
    });
